// ── PARENT: DASHBOARD ────────────────────────────────────────────────
// esc/calcAge/seasonLabel/fmt$ (shared classic scripts) stay as bare globals —
// not migrated yet. payNow (billing-shared.js) is a real import, wired via
// VIEW_INIT below instead of window/onclick.
import { DB, session, PROGRAMS } from '../core/state.js';
import { kidPrograms, isContinuingPerformer, priorityWindowOpen, priorityDeadlineLabel, enrollmentMonthlyRate, hasRateOverride } from '../core/domain-utils.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';
import { tvWindowOpen } from './catalog-shared.js';
import { programMonthly, priceLabel, enrollmentPriceLabel, payNow, programOneTimePrice } from './billing-shared.js';
import { programChip } from './admin-registrations.js';

VIEWS['p-home'] = () => {
  const kids = DB.children.filter(c => c.accountId === session.id);
  const upSeasonYr = DB.settings.upcomingSeasonYear;
  // "Enrolled" = actively in a current program OR registered for the upcoming season
  const enrolledKids = kids.filter(k =>
    kidPrograms(k).length > 0 ||
    DB.seasonEnrollments.some(se => se.performerId === k.id && se.seasonYear === upSeasonYr && se.status === 'registered'));
  const charges = DB.charges.filter(c => c.accountId === session.id);
  const due = charges.filter(c => c.status === 'due').reduce((s, c) => s + c.amount, 0);
  // Per enrollment, not per program: a performer on a reduced rate contributes
  // their own number, so this tile agrees with the group totals below it and
  // with what the 1st actually charges.
  const monthly = kids.reduce((s, k) => s + kidPrograms(k).reduce((t, pid) =>
    t + (programMonthly(PROGRAMS.find(p => p.id === pid)) ? enrollmentMonthlyRate(k.id, pid) : 0), 0), 0);

  // Re-enrollment banner — only when the priority window is open and this family
  // has at least one continuing performer who hasn't already registered for next season.
  const upSeason = DB.settings.upcomingSeasonYear;
  const continuingKids = kids.filter(k => isContinuingPerformer(k.id));
  const alreadyRegistered = continuingKids.every(k =>
    DB.seasonEnrollments.some(se => se.performerId === k.id && se.seasonYear === upSeason));
  const showReenroll = priorityWindowOpen() && continuingKids.length > 0 && !alreadyRegistered;

  // Classes banner — separate from PC/BB re-enrollment above, open to any
  // family with a performer on the account (no PC-enrollment requirement).
  const showTapVoice = tvWindowOpen() && kids.length > 0;

  return `
    <div class="page-head">
      <span class="eyebrow">Welcome back</span>
      <h2 class="serif">Hello, <em>${esc(session.firstName)}</em></h2>
    </div>
    ${due ? `
    <div class="card" style="margin-bottom:18px;border-color:rgba(248,113,113,0.35);display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap">
      <div>
        <div style="font-family:'Libre Baskerville',serif;font-size:17px;color:var(--cream)">You have a balance of ${fmt$(due)}</div>
        <div style="font-size:13px;color:var(--muted);margin-top:3px">Pay now with your card on file, or review the details under Billing.</div>
      </div>
      <button class="btn" id="payNowBtn" style="width:auto;white-space:nowrap;padding:11px 22px;flex-shrink:0" data-action="pay-now">Pay now</button>
    </div>` : ''}
    ${showReenroll ? `
    <div class="card" style="margin-bottom:18px;border-color:rgba(201,168,76,0.4);background:linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.02))">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap">
        <div>
          <div style="font-family:'Libre Baskerville',serif;font-size:18px;color:var(--cream);margin-bottom:4px">Registration for the ${seasonLabel(upSeason)} season is open</div>
          <div style="font-size:13px;color:var(--muted)">Priority registration for returning performers. Confirm which performers are continuing and reserve their spots.${priorityDeadlineLabel() ? ` <b style="color:var(--cream)">Please register by ${priorityDeadlineLabel()}.</b>` : ''}</div>
        </div>
        <button class="btn" style="width:auto;white-space:nowrap;padding:11px 22px" onclick="route('p-reenroll')">Start enrollment</button>
      </div>
    </div>` : ''}
    ${showTapVoice ? `
    <div class="card" style="margin-bottom:18px;border-color:rgba(201,168,76,0.4);background:linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.02))">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap">
        <div>
          <div style="font-family:'Libre Baskerville',serif;font-size:18px;color:var(--cream);margin-bottom:4px">Classes registration is open</div>
          <div style="font-size:13px;color:var(--muted)">Nothing is charged today — tuition bills to your card on file when classes start in September.</div>
        </div>
        <button class="btn" style="width:auto;white-space:nowrap;padding:11px 22px" onclick="route('p-tapvoice')">Register</button>
      </div>
    </div>` : ''}
    <div class="grid-3">
      <div class="stat"><div class="lab">Performers enrolled</div><div class="val">${enrolledKids.length}</div><div class="det">${enrolledKids.map(k => esc(k.name.split(' ')[0])).join(', ') || 'None yet'}</div></div>
      <div class="stat"><div class="lab">Combined monthly tuition</div><div class="val">${fmt$(monthly)}</div><div class="det">Charged per program on its billing date</div></div>
      <div class="stat"><div class="lab">Balance due</div><div class="val" style="${due ? 'color:var(--red)' : ''}">${fmt$(due)}</div><div class="det">${due ? 'Due now' : 'You’re all caught up'}</div></div>
    </div>
    ${enrollmentCards(kids)}`;
};

// Enrolments, grouped by program rather than by performer.
//
// The old table put one row per performer and repeated the program on each,
// which meant two siblings in PC printed "$95/mo" twice and never showed the
// $190 the family is actually charged -- and, once the rehearsal schedule
// arrived, would have repeated the same link once per child. Grouping by
// program fixes both: the link and the price belong to the program, so they
// render exactly once no matter how many siblings are in it.
//
// A performer in two programs deliberately appears in both groups. They are
// separate enrolments with separate money, so that is the truth, not a bug.
function programGroups(kids) {
  const groups = new Map();
  kids.forEach(k => kidPrograms(k).forEach(pid => {
    // PROGRAMS is filtered server-side to active + visible_to_parents for a
    // parent session, so an archived summer workshop resolves to undefined
    // here and drops out on its own -- no season filtering needed.
    const p = PROGRAMS.find(x => x.id === pid);
    if (!p) return;
    if (!groups.has(pid)) groups.set(pid, { program: p, kids: [] });
    groups.get(pid).kids.push(k);
  }));
  return [...groups.values()];
}

// "$95.00/mo per performer · $190.00/mo" for a shared program, the bare rate
// for a single performer. Summer workshops price one-time, so they total the
// flat rate instead.
//
// Takes the performers rather than a count because tuition is per-enrollment:
// a family where one child is on a reduced rate has no single "per performer"
// figure, so that half of the line is dropped instead of being made wrong.
// The total is always the sum of what each child is actually billed.
function groupMoney(p, kids) {
  const n = kids.length;
  const monthly = programMonthly(p);
  const mixed = monthly && kids.some(k => hasRateOverride(k.id, p.id));

  if (n < 2) return `<b>${monthly ? enrollmentPriceLabel(p, kids[0] && kids[0].id) : priceLabel(p)}</b>`;

  const total = monthly
    ? kids.reduce((s, k) => s + enrollmentMonthlyRate(k.id, p.id), 0)
    : programOneTimePrice(p) * n;
  const suffix = monthly ? '/mo' : '';

  if (mixed) return `<b>${fmt$(total)}${suffix}</b> total`;
  return `${priceLabel(p)} per performer · <b>${fmt$(total)}${suffix}</b>`;
}

function enrollmentCards(kids) {
  const groups = programGroups(kids);
  const season = groups.filter(g => g.program.isProgram);
  const classes = groups.filter(g => !g.program.isProgram);

  // Anyone who landed in no group at all. Only worth naming when there is a
  // card for the note to sit under -- "not enrolled: everyone" on an otherwise
  // empty dashboard is noise.
  const placed = new Set(groups.flatMap(g => g.kids.map(k => k.id)));
  const idle = kids.filter(k => !placed.has(k.id));
  const note = idle.length
    ? `<div class="card-note">Not enrolled this season: ${idle.map(k => `${esc(k.name)} (${calcAge(k.dob)})`).join(', ')}</div>`
    : '';

  // Both cards are independently conditional. A Tap & Voice family has no
  // season card, and the untitled card must never render without a program
  // name inside it -- that heading is the only thing explaining what it is.
  const seasonCard = season.length ? `
    <div class="card" style="margin-top:18px">
      ${season.map(g => {
        const url = (DB.settings.rehearsalScheduleUrls || {})[g.program.id] || '';
        return `
        <div class="pgroup">
          <div class="pgroup-top">
            <div class="pgroup-name">${esc(g.program.name)}</div>
            ${url ? `<a class="doc-link" href="${esc(url)}" target="_blank" rel="noopener noreferrer">Rehearsal schedule ↗</a>` : ''}
          </div>
          <div class="pgroup-kids">${g.kids.map(k => programChip(g.program.id, `${k.name.split(' ')[0]} · ${calcAge(k.dob)}`)).join('')}</div>
          <div class="pgroup-money">${groupMoney(g.program, g.kids)}</div>
        </div>`;
      }).join('')}
      ${classes.length ? '' : note}
    </div>` : '';

  const classCard = classes.length ? `
    <div class="card" style="margin-top:18px">
      <div class="card-hd">Classes &amp; Workshops</div>
      <div class="tbl-wrap"><table>
        <tr><th>Class</th><th>Performer</th><th style="text-align:right">Tuition</th></tr>
        ${classes.flatMap(g => g.kids.map(k => `
          <tr><td>${esc(g.program.name)}</td><td>${esc(k.name)}</td>
          <td class="amt" style="text-align:right">${enrollmentPriceLabel(g.program, k.id)}</td></tr>`)).join('')}
      </table></div>
      ${note}
    </div>` : '';

  return seasonCard + classCard;
}

VIEW_INIT['p-home'] = () => {
  document.querySelectorAll('[data-action="pay-now"]').forEach(btn => {
    btn.addEventListener('click', payNow);
  });
};
